import React, { useEffect, useState } from 'react';

import CONTENT from '../static-content/geo-ip-notification.json';
import locationsList from '../static-content/locale-menu.json';

const geoAccessID = 'geoip_allow';
const geoISODesignationID = 'geoip_iso';

/**
 * Exported Geo Context
 */
export const GeoAccessContext = React.createContext();

/**
 * Page level context which manages access to page content based on visitor's geographic location. The context checks the user's
 * location by using IP lookup and then sets parameters which can be used to control access to the site, or to prompt users that
 * they are accessing a site outside of their region
 *
 * Local Management
 * - If geoip_iso item has not been set, lookup the visitor's IP using Maxmind GeoIp2 https://dev.maxmind.com/geoip/geoip2/javascript/
 * - If geiup_allow item is  true, allow access
 * - If geoip_allow item is false, redirect to UK site
 *
 * Geo Conflict Prompt
 * - If geoip_allow has not been set, and the visitor's IP is outside of US region then the geoConflict flag is set to true
 * - allowAccess flag is also set to true, allowing user to browse the site's content - but the conflict will still remain
 *
 * Context Values
 * {boolean} allowAccess | flag designating if viewership of the page should be allowed based on location (or GeoISODesignation override)
 * {boolean} geoConflict | flag which designates a visitor has not set their GeoISODesignation and their IP is not in the US region
 * {function} setGeoAccess | handler function which allows Context accessors to manipulate the allowAccess flag
 *
 * @function GeoAccessContextProvider
 * @param {string} pageName The name of the page (usually the same as SEO Title)
 * @param {string} teliumEvent Event name to uniquely identify important page-view events
 * @param {string} pageType The type used to identify page templates
 */
const GeoAccessContextProvider = ({ children }) => {
  const [allowAccess, setAllowAccess] = useState('');
  const [geoConflict, setGeoConflict] = useState(false);
  const [geoLocation, setGeoLocation] = useState('');
  const geoAccessFlag = typeof window !== 'undefined' ? sessionStorage.getItem(geoAccessID) : null;
  const geoISOItem = typeof window !== 'undefined' ? localStorage.getItem(geoISODesignationID)?.toUpperCase() : null;

  /**
   * Handler function which accepts a boolean flag and set the GeoAccess item, and allowAccess context flag based
   * on the passed flag.
   *
   * @param {boolean} value
   */
  const setGeoAccess = value => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(geoAccessID, value);
      setGeoConflict(false);
      setAllowAccess(value);
    }
  };

  /**
   * Helper function used to resolve content access based on ISO and Access items. If the Access item is not
   * defined, access defaults to true, but a GeoConflict is set allowing the visitor to be prompted.
   */
  const resolveAccess = (iso, access) => {
    if (iso && access) {
      setGeoLocation(iso);
      setGeoAccess(true);
    } else {
      const siteAccess = access === 'false' ? false : true;
      const geoPrompt = typeof access === 'undefined' || access === null ? true : false;
      setAllowAccess(siteAccess);
      setGeoConflict(geoPrompt);
      setGeoLocation(iso);
    }
  };

  /**
   * Error callback handler for setting access to true if IP region lookup fails
   */
  const onError = () => {
    setAllowAccess(true);
  };

  /**
   * Success callback handler for setting the ISO item and resolving access after IP region is determined
   * @param {object} geoResponse response object from Maxmind GeoIp2 https://dev.maxmind.com/geoip/geoip2/javascript/
   */
  const onSuccess = geoResponse => {
    const code = geoResponse.country.iso_code;

    if (typeof window !== 'undefined') {
      localStorage.setItem(geoISODesignationID, code);
      resolveAccess(code, geoAccessFlag);
    }
  };

  /**
   * Recursive method which triggers the ip lookup for a visitor. If a visitor geoISO item has already been determined,
   * then access is resolved. Otherwise, a request is executed to obtain the visitor's IP in order to resolve their access
   *
   * @function useEffect
   */
  const validateGeoIp = () => {
    if (window.geoip2) {
      if (typeof geoISOItem === 'undefined' || geoISOItem === null) {
        window.geoip2.country(onSuccess, onError);
      } else {
        resolveAccess(geoISOItem, geoAccessFlag);
      }
    }
  };

  /**
   * Lifecycle method which triggers on mount of the layout - Initiates the validation of a visitor's IP and their
   * access assignments to the site
   *
   * @function useEffect
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      validateGeoIp();
    } else {
      setAllowAccess(true);
    }
  }, []);

  /**
   * Lifecycle method which monitors changes to the allowAccess context value. If the allowAccess flag is set to false at any time,
   * the user will be redirected to the UK or AUS site
   *
   * @function useEffect
   */
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !window.location.href.includes('digital-martech-maturity-health-check')
    ) {

      const urlList = locationsList.locations.map(locale => locale.url);
      const url = new URL(window.location);
      const modifiedUrlPath = url.pathname.split('/').filter(userGeo => !urlList.includes(userGeo));

      if (CONTENT.UK.GEO_LOCATIONS.includes(geoLocation)) {
        if (!url.pathname.includes('en-gb')) {
          url.pathname = 'en-gb' + modifiedUrlPath.join('/');
          window.location.replace(url.href);
        }
      } else if (CONTENT.AU.GEO_LOCATIONS.includes(geoLocation)) {
        if (!url.pathname.includes('en-au')) {
          url.pathname = 'en-au' + modifiedUrlPath.join('/');
          window.location.replace(url.href);
        }
      } else if (CONTENT.US.GEO_LOCATIONS.includes(geoLocation)) {
        if (!url.pathname.includes('en-us')) {
          url.pathname = 'en-us' + modifiedUrlPath.join('/');
          window.location.replace(url.href);
        }
      } else if (CONTENT.DE.GEO_LOCATIONS.includes(geoLocation)) {
        if (!url.pathname.includes('de-de')) {
          url.pathname = 'de-de' + modifiedUrlPath.join('/');
          window.location.replace(url.href);
        }
      } else if (CONTENT.IN.GEO_LOCATIONS.includes(geoLocation)) {
        if (!url.pathname.includes('en-in')) {
          url.pathname = 'en-in' + modifiedUrlPath.join('/');
          window.location.replace(url.href);
        }
      } else {
        return
      }
    }
  }, [geoLocation, allowAccess]);

  const defaultContext = {
    geoConflict,
    allowAccess,
    setGeoAccess
  };

  return <GeoAccessContext.Provider value={defaultContext}>{children}</GeoAccessContext.Provider>;
};

export default GeoAccessContextProvider;
